"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import Link from "next/link";
import Image from "next/image";
import type { CSSProperties, FormEvent } from "react";
import { memo, useEffect, useRef, useState } from "react";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import { Field, FieldLabel } from "@/components/ui/field";
import { EmbedScriptInit } from "@/blocks/ScriptSnippetBlock/EmbedScriptInit";
import { richTextToPlainText } from "@/blocks/block-utils";
import {
  SiFacebook,
  SiInstagram,
  SiGithub,
  SiDribbble,
  SiDiscord,
  SiX,
  SiYoutube,
  SiTiktok,
  SiThreads,
  SiLinkerd,
} from "@icons-pack/react-simple-icons";
import type { Form } from "@/payload-types";
import RichText from "@/components/RichText";
import { cn } from "@/lib/utils";

type ContactForm = Pick<
  Form,
  | "id"
  | "fields"
  | "submitButtonLabel"
  | "confirmationType"
  | "confirmationMessage"
  | "redirect"
>;

type ContactFormField = NonNullable<ContactForm["fields"]>[number];
type DateContactFormField = {
  name: string;
  label?: string | null;
  width?: number | null;
  required?: boolean | null;
  defaultValue?: string | null;
  id?: string | null;
  blockName?: string | null;
  blockType: "date";
};
type SupportedContactFormField = ContactFormField | DateContactFormField;
type FormValues = Record<string, boolean | string>;
type SocialPlatform =
  | "facebook"
  | "instagram"
  | "github"
  | "dribbble"
  | "linkedin"
  | "x"
  | "youtube"
  | "tiktok"
  | "threads"
  | "discord";
const EMPTY_FORM_FIELDS: SupportedContactFormField[] = [];

interface ContactSection6Props {
  tagline?: string;
  heading?: string;
  headingTag?: "h1" | "h2" | "h3";
  headingClassName?: string;
  description?: string;
  form?: ContactForm | null;
  nameLabel?: string;
  namePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  privacyText?: string;
  privacyLinkLabel?: string;
  privacyLinkHref?: string;
  submitButtonLabel?: string;
  embedMarkup?: string;
  officesHeading?: string;
  officesDescription?: string;
  offices?: { name: string; address: string }[];
  emailHeading?: string;
  emailDescription?: string;
  emailContacts?: { label: string; email: string }[];
  followHeading?: string;
  socialLinks?: Partial<Record<SocialPlatform, string>>;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Parens escaped so the same source works in HTML `pattern` (validated with unicodeSets / `v`).
const PHONE_PATTERN = /^\+?[0-9\s\(\)\-.]{7,20}$/;

const isValidPhoneNumber = (value: string) => {
  if (!PHONE_PATTERN.test(value)) return false;

  const digitCount = value.replace(/\D/g, "").length;
  return digitCount >= 7 && digitCount <= 15;
};

const clampFieldWidth = (width?: number | null) => {
  if (typeof width !== "number" || Number.isNaN(width)) return "100%";
  const normalized = Math.min(100, Math.max(25, width));
  return normalized >= 100 ? "100%" : `calc(${normalized}% - 1rem)`;
};

const getFieldLabel = (field: SupportedContactFormField) => {
  if (
    "label" in field &&
    typeof field.label === "string" &&
    field.label.trim()
  ) {
    return field.label;
  }

  if ("label" in field) {
    const plainTextLabel = richTextToPlainText(field.label);
    if (plainTextLabel) return plainTextLabel;
  }

  if ("name" in field && typeof field.name === "string" && field.name.trim()) {
    return field.name
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return "";
};

const getFieldPlaceholder = (field: SupportedContactFormField) => {
  if (
    "placeholder" in field &&
    typeof field.placeholder === "string" &&
    field.placeholder.trim()
  ) {
    return field.placeholder;
  }

  return undefined;
};

const getValidationFieldLabel = (field: SupportedContactFormField) => {
  if (field.blockType === "checkbox") {
    return "This field";
  }

  return getFieldLabel(field) || "This field";
};

const hasFieldName = (
  field: SupportedContactFormField,
): field is SupportedContactFormField & { name: string } => {
  return (
    "name" in field && typeof field.name === "string" && field.name.length > 0
  );
};

const getInitialValues = (fields: SupportedContactFormField[]): FormValues => {
  return fields.reduce<FormValues>((acc, field) => {
    if (!hasFieldName(field)) return acc;

    switch (field.blockType) {
      case "checkbox":
        acc[field.name] = Boolean(field.defaultValue);
        break;
      case "number":
        acc[field.name] =
          typeof field.defaultValue === "number"
            ? String(field.defaultValue)
            : "";
        break;
      case "date":
        acc[field.name] =
          typeof field.defaultValue === "string" ? field.defaultValue : "";
        break;
      case "text":
      case "textarea":
      case "email":
      case "phone":
        acc[field.name] = "";
        break;
      case "select":
        acc[field.name] =
          typeof field.defaultValue === "string" ? field.defaultValue : "";
        break;
      default:
        acc[field.name] = "";
    }

    return acc;
  }, {});
};

const validateForm = (
  fields: SupportedContactFormField[],
  values: FormValues,
): Record<string, string> => {
  return fields.reduce<Record<string, string>>((acc, field) => {
    if (!hasFieldName(field)) return acc;

    const value = values[field.name];
    const textValue = typeof value === "string" ? value.trim() : "";
    const isRequired = "required" in field ? Boolean(field.required) : false;

    if (isRequired) {
      const missingValue =
        field.blockType === "checkbox"
          ? value !== true
          : textValue.length === 0;

      if (missingValue) {
        acc[field.name] = `${getValidationFieldLabel(field)} is required.`;
        return acc;
      }
    }

    if (
      field.blockType === "email" &&
      textValue &&
      !EMAIL_PATTERN.test(textValue)
    ) {
      acc[field.name] = "Enter a valid email address.";
      return acc;
    }

    if (
      field.blockType === "phone" &&
      textValue &&
      !isValidPhoneNumber(textValue)
    ) {
      acc[field.name] = "Enter a valid phone number.";
      return acc;
    }

    if (
      field.blockType === "number" &&
      textValue &&
      Number.isNaN(Number(textValue))
    ) {
      acc[field.name] = "Enter a valid number.";
    }

    return acc;
  }, {});
};

const getSubmissionValue = (
  field: SupportedContactFormField,
  values: FormValues,
) => {
  if (!hasFieldName(field)) return null;

  const value = values[field.name];

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return typeof value === "string" ? value : "";
};

const parseErrorMessage = (error: unknown) => {
  if (!(typeof error === "object" && error)) return null;

  const payloadError = error as {
    data?: { errors?: { message?: unknown }[] };
    errors?: { message?: unknown }[];
    message?: unknown;
  };

  const nestedMessage =
    payloadError.data?.errors?.find((item) => typeof item?.message === "string")
      ?.message ??
    payloadError.errors?.find((item) => typeof item?.message === "string")
      ?.message;

  if (typeof nestedMessage === "string" && nestedMessage.trim()) {
    return nestedMessage;
  }

  return typeof payloadError.message === "string" && payloadError.message.trim()
    ? payloadError.message
    : null;
};

const getEventTargetElement = (target: EventTarget | null) => {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
};

const isInteractiveCheckboxContentTarget = (target: EventTarget | null) => {
  const element = getEventTargetElement(target);
  return Boolean(element?.closest("a, button, input, textarea, select"));
};

const StaticEmbedMarkup = memo(function StaticEmbedMarkup({
  markup,
}: {
  markup: string;
}) {
  return (
    <div
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
});

const FORM_HONEYPOT_FIELD = "__scarp_honeypot";

const scrollPageToTop = (target: HTMLElement | null) => {
  const performScroll = () => {
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    document.body.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  performScroll();
  window.requestAnimationFrame(performScroll);
};

export function ContactSection6({
  tagline,
  heading,
  headingTag = "h1",
  headingClassName = "heading-lg",
  description,
  form,
  nameLabel,
  namePlaceholder,
  emailLabel,
  emailPlaceholder,
  messageLabel,
  messagePlaceholder,
  privacyText,
  privacyLinkLabel,
  privacyLinkHref,
  submitButtonLabel,
  embedMarkup,
  officesHeading,
  officesDescription,
  offices,
  emailHeading,
  emailDescription,
  emailContacts,
  followHeading,
  socialLinks,
}: ContactSection6Props) {
  const HeadingTag = headingTag;
  const sectionRef = useRef<HTMLElement | null>(null);
  const [agreed, setAgreed] = useState(false);
  const selectedForm = form ?? null;
  const rawFormFields = selectedForm?.fields;
  const formFields: SupportedContactFormField[] = Array.isArray(rawFormFields)
    ? rawFormFields
    : EMPTY_FORM_FIELDS;
  const [formValues, setFormValues] = useState<FormValues>(() =>
    getInitialValues(formFields),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<
    "idle" | "error" | "submitting" | "success"
  >("idle");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const hasEmbedMarkup =
    typeof embedMarkup === "string" && embedMarkup.trim().length > 0;
  const officeList = Array.isArray(offices) ? offices : [];
  const contactList = Array.isArray(emailContacts) ? emailContacts : [];
  const hasSelectedForm = typeof selectedForm?.id === "string";
  const submitLabel =
    (typeof selectedForm?.submitButtonLabel === "string" &&
      selectedForm.submitButtonLabel.trim()) ||
    submitButtonLabel ||
    "Submit";
  const socialItems: {
    key: SocialPlatform;
    label: string;
    icon: typeof SiFacebook;
  }[] = [
    { key: "facebook", label: "Facebook", icon: SiFacebook },
    { key: "instagram", label: "Instagram", icon: SiInstagram },
    { key: "github", label: "GitHub", icon: SiGithub },
    { key: "dribbble", label: "Dribbble", icon: SiDribbble },
    { key: "linkedin", label: "LinkedIn", icon: SiLinkerd },
    { key: "x", label: "X", icon: SiX },
    { key: "youtube", label: "YouTube", icon: SiYoutube },
    { key: "tiktok", label: "TikTok", icon: SiTiktok },
    { key: "threads", label: "Threads", icon: SiThreads },
    { key: "discord", label: "Discord", icon: SiDiscord },
  ];
  const socialEntries = socialItems
    .map((item) => {
      const href = socialLinks?.[item.key];
      if (!href) return null;
      return { href, icon: item.icon, label: item.label, key: item.key };
    })
    .filter(Boolean) as {
    href: string;
    icon: typeof SiFacebook;
    label: string;
    key: SocialPlatform;
  }[];
  const hasPrivacyLink =
    typeof privacyText === "string" &&
    privacyText.trim().length > 0 &&
    typeof privacyLinkLabel === "string" &&
    privacyLinkLabel.trim().length > 0 &&
    typeof privacyLinkHref === "string" &&
    privacyLinkHref.trim().length > 0;

  useEffect(() => {
    setFormValues(getInitialValues(formFields));
    setFieldErrors({});
    setHoneypot("");
    setStatus("idle");
    setStatusMessage(null);
  }, [selectedForm?.id, formFields]);

  const detailsSections = [
    officeList.length || officesHeading || officesDescription ? (
      <div key="offices" className="flex flex-col gap-6">
        {officesHeading || officesDescription ? (
          <div className="flex flex-col gap-3">
            {officesHeading ? (
              <h2 className="text-foreground heading-sm">{officesHeading}</h2>
            ) : null}
            {officesDescription ? (
              <p className="text-muted-foreground text-base">
                {officesDescription}
              </p>
            ) : null}
          </div>
        ) : null}

        {officeList.length ? (
          <div className="flex flex-col gap-6 lg:flex-row">
            {officeList.map((office, index) => (
              <div
                key={`${office.name}-${index}`}
                className="flex flex-1 flex-col gap-2"
              >
                <h3 className="text-card-foreground text-base font-semibold">
                  {office.name}
                </h3>
                <p className="text-muted-foreground text-base whitespace-pre-line">
                  {office.address}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    ) : null,
    contactList.length || emailHeading || emailDescription ? (
      <div key="emails" className="flex flex-col gap-6">
        {emailHeading || emailDescription ? (
          <div className="flex flex-col gap-3">
            {emailHeading ? (
              <h2 className="text-foreground heading-sm">{emailHeading}</h2>
            ) : null}
            {emailDescription ? (
              <p className="text-muted-foreground text-base">
                {emailDescription}
              </p>
            ) : null}
          </div>
        ) : null}

        {contactList.length ? (
          <div className="flex flex-col gap-6 lg:flex-row">
            {contactList.map((contact, index) => (
              <div
                key={`${contact.label}-${index}`}
                className="flex flex-1 flex-col gap-2"
              >
                <h3 className="text-card-foreground text-base font-semibold">
                  {contact.label}
                </h3>
                <Link
                  href={`mailto:${contact.email}`}
                  className="text-muted-foreground text-base underline"
                >
                  {contact.email}
                </Link>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    ) : null,
    socialEntries.length || followHeading ? (
      <div key="socials" className="flex flex-col gap-6">
        {followHeading ? (
          <h2 className="text-foreground heading-sm">{followHeading}</h2>
        ) : null}
        {socialEntries.length ? (
          <div className="flex flex-row gap-5">
            {socialEntries.map((entry) => {
              const Icon = entry.icon;

              return (
                <Link
                  key={entry.label}
                  href={entry.href}
                  aria-label={entry.label}
                  className="text-foreground transition-all duration-300 hover:scale-110"
                >
                  {entry.key === "linkedin" ? (
                    <Image
                      src="/linkedin.svg"
                      alt=""
                      width={20}
                      height={20}
                      aria-hidden
                      className="size-6"
                    />
                  ) : (
                    <Icon className="size-6" />
                  )}
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>
    ) : null,
  ].filter(Boolean);

  const detailsContent = detailsSections.length ? (
    <div className="lg:container mx-auto flex flex-col gap-8">
      {detailsSections.map((section, index) => (
        <div key={index} className="contents">
          {index > 0 ? <Separator /> : null}
          {section}
        </div>
      ))}
    </div>
  ) : null;

  const handleSelectedFormSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!selectedForm?.id) return;

    const errors = validateForm(formFields, formValues);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setStatus("error");
      setStatusMessage("Please correct the highlighted fields and try again.");
      return;
    }

    setStatus("submitting");
    setStatusMessage(null);
    scrollPageToTop(sectionRef.current);

    try {
      const response = await fetch("/api/form-submissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          form: selectedForm.id,
          submissionData: [
            { field: FORM_HONEYPOT_FIELD, value: honeypot },
            ...formFields.flatMap((field) => {
              const value = getSubmissionValue(field, formValues);
              if (!hasFieldName(field) || value === null) return [];

              return [
                {
                  field: field.name,
                  value,
                },
              ];
            }),
          ],
        }),
      });

      const payload = (await response.json().catch(() => null)) as unknown;

      if (!response.ok) {
        throw payload;
      }

      if (
        selectedForm.confirmationType === "redirect" &&
        typeof selectedForm.redirect?.url === "string" &&
        selectedForm.redirect.url.trim()
      ) {
        window.location.assign(selectedForm.redirect.url);
        return;
      }

      setFormValues(getInitialValues(formFields));
      setHoneypot("");
      setFieldErrors({});
      setStatus("success");
      setStatusMessage(null);
    } catch (error) {
      setStatus("error");
      setStatusMessage(
        parseErrorMessage(error) ??
          "The form could not be submitted right now. Please try again.",
      );
    }
  };

  const renderSelectedField = (
    field: SupportedContactFormField,
    index: number,
  ) => {
    const width = clampFieldWidth("width" in field ? field.width : undefined);
    const fieldStyle = {
      "--field-width": width,
    } as CSSProperties;

    if (field.blockType === "message") {
      return (
        <div
          key={`message-${index}`}
          style={fieldStyle}
          className="w-full md:basis-(--field-width)"
        >
          <RichText
            data={field.message}
            enableGutter={false}
            className="text-muted-foreground"
          />
        </div>
      );
    }

    if (!hasFieldName(field)) return null;

    const label = getFieldLabel(field);
    const placeholder = getFieldPlaceholder(field);
    const error = fieldErrors[field.name];
    const value = formValues[field.name];

    if (field.blockType === "checkbox") {
      const checkboxLabelContent =
        "label" in field && typeof field.label === "object" && field.label
          ? field.label
          : null;
      const setCheckboxValue = (nextValue: boolean) => {
        setFormValues((current) => ({
          ...current,
          [field.name]: nextValue,
        }));
        setFieldErrors((current) => {
          if (!(field.name in current)) return current;
          const next = { ...current };
          delete next[field.name];
          return next;
        });
      };

      return (
        <Field
          key={field.name}
          orientation="horizontal"
          style={fieldStyle}
          className="w-full md:basis-(--field-width)"
        >
          <Checkbox
            id={field.name}
            checked={value === true}
            onCheckedChange={(checked) => setCheckboxValue(checked === true)}
            aria-required={field.required ?? undefined}
            aria-invalid={error ? "true" : "false"}
            aria-label="Agree to Privacy Policy"
          />
          <div className="flex flex-1 flex-col gap-1">
            {checkboxLabelContent ? (
              <div
                className="text-muted-foreground inline cursor-pointer text-sm leading-normal font-normal"
                onMouseDown={(event) => {
                  if (isInteractiveCheckboxContentTarget(event.target)) {
                    return;
                  }

                  event.preventDefault();
                }}
                onClick={(event) => {
                  if (isInteractiveCheckboxContentTarget(event.target)) {
                    return;
                  }

                  setCheckboxValue(value !== true);
                }}
              >
                <RichText
                  data={checkboxLabelContent}
                  enableGutter={false}
                  className="text-muted-foreground [&_p]:mb-0! [&_p]:pb-0!"
                />
              </div>
            ) : (
              <FieldLabel
                htmlFor={field.name}
                className="text-muted-foreground inline text-sm leading-none font-normal"
              >
                {label}
              </FieldLabel>
            )}
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
          </div>
        </Field>
      );
    }

    if (field.blockType === "select") {
      return (
        <Field
          key={field.name}
          style={fieldStyle}
          className="w-full md:basis-(--field-width)"
        >
          <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
          <select
            id={field.name}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => {
              setFormValues((current) => ({
                ...current,
                [field.name]: event.target.value,
              }));
              setFieldErrors((current) => {
                if (!(field.name in current)) return current;
                const next = { ...current };
                delete next[field.name];
                return next;
              });
            }}
            required={field.required ?? undefined}
            aria-invalid={error ? "true" : "false"}
            className={cn(
              "border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-12 rounded-sm border bg-transparent px-4 py-2 text-base outline-none md:text-sm",
              "w-full min-w-0 appearance-none",
              error ? "ring-destructive/20" : "",
            )}
          >
            <option value="">
              {field.placeholder || `Select ${label.toLocaleLowerCase()}`}
            </option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </Field>
      );
    }

    const inputType =
      field.blockType === "email"
        ? "email"
        : field.blockType === "phone"
          ? "tel"
        : field.blockType === "date"
          ? "date"
        : field.blockType === "number"
          ? "number"
          : "text";

    if (
      field.blockType === "text" ||
      field.blockType === "email" ||
      field.blockType === "phone" ||
      field.blockType === "date" ||
      field.blockType === "number" ||
      field.blockType === "country" ||
      field.blockType === "state"
    ) {
      return (
        <Field
          key={field.name}
          style={fieldStyle}
          className="w-full md:basis-(--field-width)"
        >
          <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
          <Input
            id={field.name}
            type={inputType}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => {
              setFormValues((current) => ({
                ...current,
                [field.name]: event.target.value,
              }));
              setFieldErrors((current) => {
                if (!(field.name in current)) return current;
                const next = { ...current };
                delete next[field.name];
                return next;
              });
            }}
            required={field.required ?? undefined}
            aria-required={field.required ?? undefined}
            aria-invalid={error ? "true" : "false"}
            placeholder={placeholder}
            inputMode={field.blockType === "phone" ? "tel" : undefined}
            pattern={field.blockType === "phone" ? PHONE_PATTERN.source : undefined}
          />
          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </Field>
      );
    }

    if (field.blockType === "textarea") {
      return (
        <Field
          key={field.name}
          style={fieldStyle}
          className="w-full md:basis-(--field-width)"
        >
          <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
          <Textarea
            id={field.name}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => {
              setFormValues((current) => ({
                ...current,
                [field.name]: event.target.value,
              }));
              setFieldErrors((current) => {
                if (!(field.name in current)) return current;
                const next = { ...current };
                delete next[field.name];
                return next;
              });
            }}
            required={field.required ?? undefined}
            aria-required={field.required ?? undefined}
            aria-invalid={error ? "true" : "false"}
            placeholder={placeholder}
          />
          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </Field>
      );
    }

    return null;
  };

  const selectedFormBody =
    status === "success" && selectedForm?.confirmationType !== "redirect" ? (
      <div className="border-border bg-muted/30 flex flex-col gap-4 rounded-sm border p-6">
        {selectedForm?.confirmationMessage ? (
          <RichText
            data={selectedForm.confirmationMessage}
            enableGutter={false}
          />
        ) : (
          <p className="text-base text-muted-foreground">
            Your message has been sent successfully.
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setStatus("idle");
            setStatusMessage(null);
          }}
        >
          Send another message
        </Button>
      </div>
    ) : (
      <>
        <div className="flex flex-col gap-5 md:flex-row md:flex-wrap">
          {formFields.map((field, index) => renderSelectedField(field, index))}
        </div>

        {statusMessage ? (
          <p
            className={cn(
              "text-sm",
              status === "error" ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {statusMessage}
          </p>
        ) : null}

        <Field>
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Sending..." : submitLabel}
          </Button>
        </Field>
      </>
    );

  return (
    <section
      ref={sectionRef}
      className="bg-background flex min-h-screen flex-col gap-8 lg:gap-12 container-padding-x container m-auto"
      aria-labelledby="contact-heading"
    >
      <div className="grid grid-cols-1 gap-32 lg:grid-cols-2 lg:gap-12">
        <div className="w-full lg:container-padding-x items-center justify-center lg:p-0">
          <div className="mx-auto flex flex-col gap-8">
            {tagline || heading || description ? (
              <div className="section-title-gap-lg flex flex-col">
                {tagline ? <Tagline>{tagline}</Tagline> : null}
                {heading ? (
                  <HeadingTag id="contact-heading" className={headingClassName}>
                    {heading}
                  </HeadingTag>
                ) : null}
                {description ? (
                  <p className="text-base text-muted-foreground">
                    {description}
                  </p>
                ) : null}
              </div>
            ) : null}

            {hasSelectedForm ? (
              <form
                className="flex flex-col gap-5"
                onSubmit={handleSelectedFormSubmit}
                aria-label="Contact form"
              >
                <input
                  type="text"
                  name={FORM_HONEYPOT_FIELD}
                  value={honeypot}
                  onChange={(event) => setHoneypot(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] top-auto h-px w-px overflow-hidden opacity-0"
                />
                {selectedFormBody}
              </form>
            ) : (
              <form
                className="flex flex-col"
                onSubmit={(event) => event.preventDefault()}
                aria-label="Contact form"
              >
                <div className="flex flex-col gap-5">
                  {nameLabel ? (
                    <Field>
                      <FieldLabel htmlFor="name">{nameLabel}</FieldLabel>
                      <Input
                        id="name"
                        placeholder={namePlaceholder}
                        required
                        aria-required="true"
                      />
                    </Field>
                  ) : null}

                  {emailLabel ? (
                    <Field>
                      <FieldLabel htmlFor="email">{emailLabel}</FieldLabel>
                      <Input
                        id="email"
                        type="email"
                        placeholder={emailPlaceholder}
                        required
                        aria-required="true"
                      />
                    </Field>
                  ) : null}

                  {messageLabel ? (
                    <Field>
                      <FieldLabel htmlFor="message">{messageLabel}</FieldLabel>
                      <Textarea
                        id="message"
                        placeholder={messagePlaceholder}
                        required
                        aria-required="true"
                      />
                    </Field>
                  ) : null}

                  {hasPrivacyLink ? (
                    <Field orientation="horizontal">
                      <Checkbox
                        id="privacy"
                        checked={agreed}
                        onCheckedChange={(checked) =>
                          setAgreed(checked === true)
                        }
                        required
                        aria-required="true"
                        aria-label="Agree to Privacy Policy"
                      />
                      <FieldLabel
                        htmlFor="privacy"
                        className="text-muted-foreground inline text-sm leading-none font-normal"
                      >
                        {privacyText}{" "}
                        <Link
                          href={privacyLinkHref}
                          className="text-primary underline"
                        >
                          {privacyLinkLabel}
                        </Link>
                        .
                      </FieldLabel>
                    </Field>
                  ) : null}

                  {submitButtonLabel ? (
                    <Field>
                      <Button type="submit" className="w-full" size="lg">
                        {submitButtonLabel}
                      </Button>
                    </Field>
                  ) : null}
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="w-full lg:px-5">
          {hasEmbedMarkup ? (
            <StaticEmbedMarkup markup={embedMarkup} />
          ) : detailsContent ? (
            <div className="lg:bg-muted/40 lg:px-5 lg:py-8">
              {detailsContent}
            </div>
          ) : null}
        </div>
      </div>

      {hasEmbedMarkup && detailsContent ? (
        <>
          <Separator />
          <div className="w-full lg:bg-muted/40 lg:px-5 lg:py-10">
            {detailsContent}
          </div>
        </>
      ) : null}
      {hasEmbedMarkup ? <EmbedScriptInit /> : null}
    </section>
  );
}
